import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { processSteps } from '../mock';

const ProcessSlider = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  useEffect(() => {
    if (!isAutoPlaying) return;
    
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % processSteps.length);
    }, 4000); // Auto-slide every 4 seconds

    return () => clearInterval(interval);
  }, [isAutoPlaying]);

  const goToPrevious = () => {
    setIsAutoPlaying(false);
    setCurrentIndex((prev) => (prev - 1 + processSteps.length) % processSteps.length);
  };

  const goToNext = () => {
    setIsAutoPlaying(false);
    setCurrentIndex((prev) => (prev + 1) % processSteps.length);
  };

  const goToSlide = (index) => {
    setIsAutoPlaying(false);
    setCurrentIndex(index);
  };

  return (
    <section className="py-20 bg-gradient-to-br from-[#093336]/5 to-white">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-[#093336] mb-4">Our Simple Process</h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            We make trademark registration easy and straightforward. Here's how it works.
          </p>
        </div>

        {/* Desktop Grid View */}
        <div className="hidden md:grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
          {processSteps.map((step, index) => (
            <div key={step.id} className="relative">
              <div className="bg-white rounded-2xl shadow-lg p-8 h-full hover:shadow-xl transition-shadow">
                <div className="w-20 h-20 bg-[#093336] text-white rounded-full flex items-center justify-center text-3xl font-bold mb-6 mx-auto">
                  {index + 1}
                </div>
                <h3 className="font-bold text-[#093336] mb-3 text-center text-xl">{step.title}</h3>
                <p className="text-gray-600 text-center leading-relaxed">{step.description}</p>
              </div>
              {index < processSteps.length - 1 && (
                <div className="hidden lg:block absolute top-14 -right-4 w-8 h-1 bg-[#F77F00]"></div>
              )}
            </div>
          ))}
        </div>

        {/* Mobile Slider View */}
        <div className="md:hidden max-w-md mx-auto relative">
          <div className="bg-white rounded-2xl shadow-xl p-8 min-h-[320px] flex flex-col justify-center">
            <div className="w-20 h-20 bg-[#093336] text-white rounded-full flex items-center justify-center text-3xl font-bold mb-6 mx-auto">
              {currentIndex + 1}
            </div>
            
            <h3 className="font-bold text-[#093336] text-xl mb-3 text-center">
              {processSteps[currentIndex].title}
            </h3>
            
            <p className="text-gray-700 text-center">
              {processSteps[currentIndex].description}
            </p>
          </div>

          {/* Navigation Arrows */}
          <button
            onClick={goToPrevious}
            className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 bg-white hover:bg-gray-50 text-[#093336] rounded-full p-2 shadow-lg transition-all"
            aria-label="Previous step"
          >
            <ChevronLeft size={20} />
          </button>
          
          <button
            onClick={goToNext}
            className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 bg-white hover:bg-gray-50 text-[#093336] rounded-full p-2 shadow-lg transition-all"
            aria-label="Next step"
          >
            <ChevronRight size={20} />
          </button>

          {/* Dots Navigation */}
          <div className="flex justify-center gap-2 mt-6">
            {processSteps.map((_, index) => (
              <button
                key={index}
                onClick={() => goToSlide(index)}
                className={`w-2.5 h-2.5 rounded-full transition-all ${
                  index === currentIndex ? 'bg-[#093336] w-6' : 'bg-gray-300'
                }`}
                aria-label={`Go to step ${index + 1}`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProcessSlider;
